import { useEffect, useReducer } from 'react'
import { adaptRealPayloadToDashboardPayload } from './adaptPayload'
import { getEmbeddedPayload } from '../utils/embeddedPayload'
import type { DashboardPayload, RealDashboardPayload } from '../types/payload'

const AVAILABLE_PERIODS = ['2026-04', '2026-05', '2026-06', '2026-07', '2026-08', '2026-09'] as const
const DEFAULT_PERIOD = AVAILABLE_PERIODS[AVAILABLE_PERIODS.length - 1]
// AVAILABLE_PERIODS is chronologically ascending, so the last 2 are the most
// recent — the header picker only ever offers a rolling 2-edition window.
const VISIBLE_EDITIONS = AVAILABLE_PERIODS.slice(-2)
const PERIOD_FILE_SUFFIX: Record<(typeof AVAILABLE_PERIODS)[number], string> = {
  '2026-04': 'abr26',
  '2026-05': 'mai26',
  '2026-06': 'jun26',
  '2026-07': 'jul26',
  '2026-08': 'ago26',
  '2026-09': 'set26',
}

type State =
  | { status: 'loading' }
  | { status: 'success'; data: DashboardPayload }
  | { status: 'error'; message: string }

type Action =
  | { type: 'FETCH_SUCCESS'; payload: DashboardPayload }
  | { type: 'FETCH_ERROR'; message: string }

function reducer(_: State, action: Action): State {
  switch (action.type) {
    case 'FETCH_SUCCESS':
      return { status: 'success', data: action.payload }
    case 'FETCH_ERROR':
      return { status: 'error', message: action.message }
  }
}

function resolvePayloadUrl(period?: string): string {
  const selectedPeriod = (period ?? DEFAULT_PERIOD) as (typeof AVAILABLE_PERIODS)[number]
  const fileSuffix = PERIOD_FILE_SUFFIX[selectedPeriod]

  if (!fileSuffix) {
    throw new Error(`No payload file configured for period "${selectedPeriod}"`)
  }

  return `/data/dashboard_payload_${fileSuffix}.json`
}

function isLegacyDashboardPayload(value: unknown): value is DashboardPayload {
  if (!value || typeof value !== 'object') {
    return false
  }

  const payload = value as Partial<DashboardPayload>
  return Boolean(payload.meta && payload.sections)
}

function isRealDashboardPayload(value: unknown): value is RealDashboardPayload {
  if (!value || typeof value !== 'object') {
    return false
  }

  const payload = value as Partial<RealDashboardPayload>
  return Boolean(payload.metrics && payload.qualityReport)
}

export function usePayload(period?: string) {
  const [state, dispatch] = useReducer(reducer, { status: 'loading' } satisfies State)

  useEffect(() => {
    let cancelled = false

    async function fetchPayload(): Promise<unknown> {
      const url = resolvePayloadUrl(period)
      const res = await fetch(url, { cache: 'no-cache' })

      if (!res.ok) {
        throw new Error(`HTTP ${res.status} — ${res.statusText}`)
      }

      return await res.json() as unknown
    }

    function buildEditionNavigation(currentPeriod: string, embedded: boolean) {
      if (embedded) {
        return {
          current: currentPeriod,
          editions: [currentPeriod],
          previous: null,
          next: null,
          next_available: false,
        }
      }

      const index = AVAILABLE_PERIODS.indexOf(currentPeriod as (typeof AVAILABLE_PERIODS)[number])

      return {
        current: currentPeriod,
        editions: [...VISIBLE_EDITIONS],
        previous: index > 0 ? AVAILABLE_PERIODS[index - 1] : null,
        next: index >= 0 && index < AVAILABLE_PERIODS.length - 1 ? AVAILABLE_PERIODS[index + 1] : null,
        next_available: index < AVAILABLE_PERIODS.length - 1,
      }
    }

    async function load() {
      try {
        // Standalone export: window.__INMP_EMBEDDED_PAYLOAD__ is assigned by
        // a plain synchronous <script> tag in exportToStandaloneHtml.ts,
        // before the app bundle's own script runs — so it's already present
        // by the time this effect fires. fetchPayload() must never run in
        // this branch: a file:// document has no server to fetch from, and
        // an unresolved fetch() would leave `status` stuck on 'loading'.
        const embeddedPayload = getEmbeddedPayload()
        const json = embeddedPayload !== undefined ? embeddedPayload : await fetchPayload()

        if (!isLegacyDashboardPayload(json) && !isRealDashboardPayload(json)) {
          throw new Error('Payload structure invalid: unsupported payload format')
        }

        const payload = isLegacyDashboardPayload(json)
          ? json
          : adaptRealPayloadToDashboardPayload(json)
        const currentPeriod = payload.meta.period || period || DEFAULT_PERIOD

        if (!cancelled) {
          dispatch({
            type: 'FETCH_SUCCESS',
            payload: {
              ...payload,
              edition_navigation: buildEditionNavigation(currentPeriod, embeddedPayload !== undefined),
            },
          })
        }
      } catch (err) {
        if (!cancelled) {
          dispatch({
            type: 'FETCH_ERROR',
            message: err instanceof Error ? err.message : 'Unknown error',
          })
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [period])

  return state
}
